<script lang="ts" setup>
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { useAttrs, useSlots, useTemplateRef, watchEffect } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import { InputSizeVariant, InputWidthVariant } from '../InputWrapper/';
  import { clearInputIconClass } from '../InputWrapper/InputWrapper.css.ts';
  import InputWrapper from '../InputWrapper/InputWrapper.vue';
  import type { TextInputProps } from './TextInput.types.ts';

  const props = withDefaults(defineProps<TextInputProps>(), {
    optionalLabel: false,
    disabled: false,
    type: 'text',
    placeholder: '',
    meta: undefined,
    width: InputWidthVariant.FULL,
    size: InputSizeVariant.DEFAULT,
  });

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    blur: [value: string];
    focus: [];
    onEnter: [];
    onTab: [];
    focusout: [];
    clear: [];
  }>();

  defineOptions({
    inheritAttrs: false,
  });
  const attrs = useAttrs();

  const slots = useSlots();

  const input = useTemplateRef<HTMLInputElement>('input');

  watchEffect(() => {
    if (props.autofocus && input.value) {
      input.value.focus();
    }
  });

  const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    let inputValue = target.value;

    // Replace tabs with spaces
    if (inputValue.includes('\t')) {
      const normalized = inputValue.replace(/\t/g, ' ');
      if (normalized !== inputValue) {
        target.value = normalized;
        inputValue = normalized;
      }
    }

    if (props.pattern) {
      // Extract the character match pattern from the pattern (e.g., "[a-zA-Z0-9\s]*" -> "[a-zA-Z0-9\s]")
      const charClassMatch = props.pattern.match(/\[([^\]]+)\]/);
      if (charClassMatch) {
        const charMatchPattern = charClassMatch[0];
        // Create a regex to match only valid characters
        const validCharRegex = new RegExp(charMatchPattern, 'g');

        // Filter the input to keep only valid characters
        const filteredValue = (inputValue.match(validCharRegex) || []).join('');

        if (filteredValue !== inputValue) {
          target.value = filteredValue;
          inputValue = filteredValue;
        }
      }
    }
    emit('update:modelValue', inputValue);
  };

  const onClear = () => {
    emit('update:modelValue', '');
    emit('clear');
  };
</script>

<template>
  <InputWrapper
    :id="id"
    :error="error"
    :warning="warning"
    :label="label"
    :loading="loading"
    :meta="meta"
    :width="width"
    :size="size"
    :readonly="readonly"
    :optional-label="optionalLabel"
  >
    <template #inputLabel> <slot name="inputLabel"></slot> </template>
    <template #inputError> <slot name="inputError"></slot> </template>
    <template #inputWarning> <slot name="inputWarning"></slot> </template>
    <template #iconLeft> <slot name="iconLeft"></slot> </template>
    <template #iconRight>
      <slot v-if="slots.iconRight" name="iconRight"></slot>
      <FontIcon
        @click="onClear"
        v-if="!readonly && clearable && modelValue"
        clickable
        :icon-name="TUNDR_ICONS.CLOSE"
        :class="clearInputIconClass"
      />
    </template>
    <template #default="slotProps">
      <input
        v-bind="attrs"
        ref="input"
        :id="id"
        :aria-required="!optionalLabel"
        :class="slotProps.slotClass"
        :data-test-id="testId"
        :disabled="disabled || loading"
        :name="name"
        :pattern="pattern"
        :readonly="readonly"
        :aria-readonly="readonly"
        :placeholder="placeholder"
        :type="type"
        :autofocus="autofocus"
        :value="modelValue"
        @blur="emit('blur', ($event.target as HTMLInputElement)?.value ?? '')"
        @focus="emit('focus')"
        @focusout="emit('focusout')"
        @keydown.enter="emit('onEnter')"
        @keydown.tab="emit('onTab')"
        @input="onInput"
      >
    </template>
  </InputWrapper>
</template>
ne
